import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { assetsApi } from '../services/api';
import type { Asset, AssetForm, TransactionForm, AssetType } from '../types';

export function useAssets(filters?: { type?: AssetType; sector?: string }) {
  return useQuery<Asset[]>({
    queryKey: ['assets', filters],
    queryFn: () => assetsApi.getAll(filters),
  });
}

export function useAsset(id: string) {
  return useQuery<Asset>({
    queryKey: ['assets', id],
    queryFn: () => assetsApi.getById(id),
    enabled: !!id,
  });
}

export function useAssetPerformance(id: string) {
  return useQuery({
    queryKey: ['assets', id, 'performance'],
    queryFn: () => assetsApi.getPerformance(id),
    enabled: !!id,
  });
}

export function useAssetHistory(id: string) {
  return useQuery({
    queryKey: ['assets', id, 'history'],
    queryFn: () => assetsApi.getHistory(id),
    enabled: !!id,
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AssetForm) => assetsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      toast.success('Ativo cadastrado com sucesso!');
    },
  });
}

export function useUpdateAsset(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<AssetForm>) => assetsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      toast.success('Ativo atualizado!');
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assetsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      toast.success('Ativo removido!');
    },
  });
}

export function useAddTransaction(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TransactionForm) => assetsApi.addTransaction(assetId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets', assetId] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      toast.success('Transação registrada!');
    },
  });
}

export function useRefreshPrices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: assetsApi.refreshPrices,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      toast.success(`Preços atualizados: ${data.updated} ativos`);
    },
  });
}
